import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import { useAppBridge } from '@shopify/app-bridge-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ModalContext } from '../../providers/ModalContext';
import { ToastContext } from '../../providers/ToastContext';
import { SheetContext } from '../../providers/SheetContext';
import { PlanContext } from '../../providers/UpgradePlanContext';
import { fetcher } from '../../lib/fetcher';
import { File as MediaFile } from '../../models/file';
import { getIconByMediaType } from '../../utils/media';
import { formatDuration, formatSize } from '../../utils/currency';
import { userLoggedInFetch } from '../../../pages/_app';
import { useUploader, GetUploadParams, UploadItem } from '../../hooks/useUploader';
import { SPEED_STOPS } from '../../models/voice-tag';
import { VOICE_TAG_VOICES } from '../../../../server/media/voice-tag.constants';
// Import Polaris web component types
import '../../../types/polaris-web-components.d';

interface UploadMediaProps {
  visible: boolean;
  onClose?: () => void;
}

type TabId = 'my-device' | 'uploaded' | 'generate';
type TypeFilter = 'all' | 'audio' | 'image' | 'application';

const TYPE_CHIPS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'audio', label: 'Audio' },
  { id: 'image', label: 'Images' },
  { id: 'application', label: 'Archives' },
];

// Prebuilt ElevenLabs Voice Library voices: no merchant recording and no voice
// cloning consent flow, which is why v1 offers these rather than the merchant's
// own voice.
const VOICE_TAG_NAME = 'voicetag.mp3';

/** One generated take the merchant can audition before committing to it. */
interface VoiceTagTake {
  fileId: string;
  url: string;
  bytes: number;
}

let modalCounter = 0;

// App Bridge's modal controller and the <s-modal> element are untyped globals;
// narrow to the members we touch. `.show`/`.hide` reject asynchronously when the
// modal isn't registered, so their return is treated as a possible promise.
interface AppBridgeModalApi {
  show?: (id: string) => unknown;
  hide?: (id: string) => unknown;
}
interface ModalOverlayElement extends HTMLElement {
  showOverlay?: () => void;
  hideOverlay?: () => void;
}
const getModalApi = (): AppBridgeModalApi | undefined =>
  // Library boundary: window.shopify is injected by App Bridge, not typed here.
  (window as unknown as { shopify?: { modal?: AppBridgeModalApi } }).shopify?.modal;
const isThenable = (value: unknown): value is PromiseLike<unknown> => {
  if (typeof value !== 'object' || value === null || !('then' in value)) return false;
  const { then } = value;
  return typeof then === 'function';
};

// ── Directory-drop traversal (webkitGetAsEntry / FileSystem API) ──
// Reads every entry of a directory; readEntries returns in batches and must be
// called repeatedly until it yields an empty array.
const readAllEntries = (reader: any): Promise<any[]> =>
  new Promise((resolve) => {
    const all: any[] = [];
    const readBatch = () =>
      reader.readEntries(
        (batch: any[]) => {
          if (!batch.length) {
            resolve(all);
            return;
          }
          all.push(...batch);
          readBatch();
        },
        () => resolve(all),
      );
    readBatch();
  });

const entryToFiles = async (entry: any): Promise<File[]> => {
  if (!entry) return [];
  if (entry.isFile) {
    return new Promise<File[]>((resolve) =>
      entry.file(
        (f: File) => resolve([f]),
        () => resolve([]),
      ),
    );
  }
  if (entry.isDirectory) {
    const children = await readAllEntries(entry.createReader());
    const nested = await Promise.all(children.map(entryToFiles));
    return nested.reduce<File[]>((acc, arr) => acc.concat(arr), []);
  }
  return [];
};

const collectFilesFromEntries = async (entries: any[]): Promise<File[]> => {
  const nested = await Promise.all(entries.map(entryToFiles));
  return nested.reduce<File[]>((acc, arr) => acc.concat(arr), []);
};

const UploadMediaModal: React.FC<UploadMediaProps> = ({ visible, onClose }) => {
  const { modal, hide } = useContext(ModalContext);
  const { show } = useContext(ToastContext);
  const { show: showSheet } = useContext(SheetContext);
  const { plan } = useContext(PlanContext);
  const app = useAppBridge();
  const router = useRouter();

  const close = onClose ?? hide;

  const [modalId] = useState(() => `upload-media-modal-${++modalCounter}`);

  // Standalone mode (dashboard / Sheet pass onClose): upload-only, so ignore any
  // stale picker fields (accept/type/primaryAction/title) left in ModalContext.
  const isStandalone = !!onClose;
  const heading = (!isStandalone && typeof modal?.title === 'string' && modal.title) || 'Add media';
  const accept: string | undefined =
    !isStandalone && typeof modal?.accept === 'string' ? modal.accept : undefined;
  const modalType: string | null = !isStandalone ? modal?.type ?? null : null;
  const primaryAction = !isStandalone ? modal?.primaryAction : undefined;
  const pickerContext = !!accept;

  // Plan gating
  const currentPlan = plan?.currentPlan;
  const planLoaded = !!currentPlan;
  const canBulk = !!currentPlan?.bulkUpload?.isEnabled;
  const maxFiles = canBulk ? 20 : 1;
  const sizeLimitMb: number = currentPlan?.files?.sizeLimit ?? 0;
  const maxSizeBytes = sizeLimitMb ? sizeLimitMb * 1_000_000 : undefined;
  const planName: string | undefined = currentPlan?.name;
  const showUpsell = !!planName && planName !== 'Pro';

  // The active chip corresponds to modal.type when the caller constrained the
  // picker to a type ('image' for artwork, 'audio'/'application' for files).
  const defaultChip: TypeFilter =
    modalType === 'audio' || modalType === 'image' || modalType === 'application'
      ? modalType
      : 'all';

  // Uploaded-tab data (picker context only). Always fetch every done file and
  // filter client-side so the type chips can browse across all media types.
  const { data: uploadedFiles } = useSWR<MediaFile[]>(
    pickerContext ? `${process.env.APP_URL}/api/files?limit=500&status=done` : null,
    (input) => fetcher(app, input),
  );

  // ── Upload params (server contract preserved from the old getUploadParams) ──
  const getUploadParams = useCallback<GetUploadParams>(
    async ({ file, meta }) => {
      const { name, type, size, lastModifiedDate, duration } = meta;
      const request = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, type, size, lastModifiedDate, duration }),
      };
      const result = await userLoggedInFetch(app)(`${process.env.APP_URL}/api/files`, request);
      if (result.status !== 201) {
        const body = await result.json();
        show({ message: body.message, isError: true });
        return Promise.reject(body.message);
      }
      const { url, fields, method, headers } = await result.json();
      return { url, fields, method, body: file, headers };
    },
    [app, show],
  );

  const { items, addFiles, removeFile, clearAll, startUpload, cancel, retry, isUploading } =
    useUploader({
      getUploadParams,
      onAllDone: (metas) => {
        showSheet({
          newUploads: metas.map((metadata) => ({ status: 'pending', metadata })),
        });
        close();
      },
      maxFiles,
      maxSizeBytes,
    });

  const uploadableCount = items.filter(
    (it) => it.status === 'staged' || it.status === 'error' || it.status === 'aborted',
  ).length;

  // Local object-URL previews for staged images, so the "My device" list shows
  // the actual photo instead of a placeholder icon. Created once per item (kept
  // in a ref, reused across progress ticks) with a version bump to re-render on
  // add/remove; revoked when an item leaves or the modal unmounts.
  const previewUrlsRef = useRef<Map<string, string>>(new Map());
  const [, bumpPreviews] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (typeof URL === 'undefined' || !URL.createObjectURL) return;
    const previews = previewUrlsRef.current;
    const liveIds = new Set(items.map((it) => it.id));
    let changed = false;
    items.forEach((it) => {
      if (it.meta.type.startsWith('image/') && !previews.has(it.id)) {
        previews.set(it.id, URL.createObjectURL(it.file));
        changed = true;
      }
    });
    previews.forEach((url, id) => {
      if (!liveIds.has(id)) {
        URL.revokeObjectURL(url);
        previews.delete(id);
        changed = true;
      }
    });
    if (changed) bumpPreviews();
  }, [items]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL?.(url));
      previewUrlsRef.current.clear();
    },
    [],
  );

  // ── Tabs (default-tab decision made once after the first fetch resolves) ──
  const [activeTab, setActiveTab] = useState<TabId>('my-device');
  const userSwitchedRef = useRef(false);
  const defaultDecidedRef = useRef(false);

  useEffect(() => {
    if (!defaultDecidedRef.current && pickerContext && uploadedFiles) {
      defaultDecidedRef.current = true;
      // Never flip away once the user has already staged files on My device.
      if (uploadedFiles.length > 0 && !userSwitchedRef.current && items.length === 0) {
        setActiveTab('uploaded');
      }
    }
  }, [pickerContext, uploadedFiles, items.length]);

  const selectTab = useCallback((tab: TabId) => {
    userSwitchedRef.current = true;
    setActiveTab(tab);
  }, []);

  // ── Generate tab (spoken voice tag) ──
  // Only offered for an audio picker; an artwork picker has nothing to speak.
  const canGenerate = pickerContext && modalType === 'audio';
  const [tagText, setTagText] = useState('');
  const [voiceId, setVoiceId] = useState(VOICE_TAG_VOICES[0].id);
  const [speed, setSpeed] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [takes, setTakes] = useState<VoiceTagTake[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const takeAudio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => takeAudio.current?.pause(), []);

  /**
   * One shared element rather than an `<audio controls>` per row: native
   * controls look different in every browser, and two rows playing at once is
   * the fastest way to make a merchant think the feature is broken.
   */
  const toggleTake = useCallback(
    (take: VoiceTagTake) => {
      let audio = takeAudio.current;
      if (!audio) {
        audio = new Audio();
        audio.addEventListener('ended', () => setPlayingId(null));
        takeAudio.current = audio;
      }
      if (playingId === take.fileId) {
        audio.pause();
        setPlayingId(null);
        return;
      }
      audio.src = take.url;
      void audio.play();
      setPlayingId(take.fileId);
    },
    [playingId],
  );

  const generateTakes = useCallback(async () => {
    const text = tagText.trim();
    if (!text) return;
    takeAudio.current?.pause();
    setPlayingId(null);
    setGenerating(true);
    try {
      const result = await userLoggedInFetch(app)(
        `${process.env.APP_URL}/api/files/generate/voice-tag`,
        {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, voiceId, speed }),
        },
      );
      const body = await result.json();
      if (result.status >= 400) {
        // The daily allowance replies with a merchant-readable message; show it.
        show({ message: body?.message || 'Could not generate a voice tag.', isError: true });
        return;
      }
      setTakes(body?.takes || []);
    } catch {
      show({ message: 'Could not generate a voice tag.', isError: true });
    } finally {
      setGenerating(false);
    }
  }, [app, show, tagText, voiceId, speed]);

  const useTake = useCallback(
    (take: VoiceTagTake) => {
      // The row stays `pending` until the media pipeline probes it, so hand the
      // picker the id plus just enough metadata to render a label.
      primaryAction?.onAction?.({
        _id: take.fileId,
        type: 'audio',
        status: 'pending',
        metadata: { name: VOICE_TAG_NAME, type: 'audio/mpeg', size: take.bytes },
      });
      close();
    },
    [primaryAction, close],
  );

  // ── Reset staged state when the modal transitions closed → open ──
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      clearAll();
      setSearch('');
      setTypeFilter(defaultChip);
      userSwitchedRef.current = false;
      defaultDecidedRef.current = false;
      setActiveTab('my-device');
      setTagText('');
      setTakes([]);
    }
    prevVisibleRef.current = visible;
  }, [visible, clearAll]);

  const handleHide = useCallback(() => close(), [close]);
  const modalElRef = useRef<ModalOverlayElement | null>(null);
  const setModalRef = useCallback(
    (el: HTMLElement | null) => {
      if (modalElRef.current) modalElRef.current.removeEventListener('hide', handleHide);
      modalElRef.current = el;
      if (el) el.addEventListener('hide', handleHide);
    },
    [handleHide],
  );

  // ── App Bridge modal open/close (falls back to the element's overlay methods
  //    when window.shopify.modal is unavailable) ──
  useEffect(() => {
    const modalApi = getModalApi();
    const el = modalElRef.current;
    // App Bridge throws ("Modal with ID … not found") when the <s-modal> hasn't
    // registered yet (mount/visible race) or was already torn down. The throw is
    // both synchronous AND surfaced as a rejected promise, so guard both paths
    // and fall back to the element's own overlay methods rather than letting it
    // reach Sentry as an uncaught error / unhandled rejection.
    const fallback = () => {
      if (visible) el?.showOverlay?.();
      else el?.hideOverlay?.();
    };
    try {
      const result = visible
        ? modalApi?.show
          ? modalApi.show(modalId)
          : el?.showOverlay?.()
        : modalApi?.hide
          ? modalApi.hide(modalId)
          : el?.hideOverlay?.();
      if (isThenable(result)) result.then(undefined, fallback);
    } catch {
      fallback();
    }
  }, [visible, modalId]);

  useEffect(() => {
    return () => {
      const modalApi = getModalApi();
      const el = modalElRef.current;
      try {
        const result = modalApi?.hide ? modalApi.hide(modalId) : el?.hideOverlay?.();
        if (isThenable(result)) result.then(undefined, () => el?.hideOverlay?.());
      } catch {
        el?.hideOverlay?.();
      }
    };
  }, [modalId]);

  // ── Drop zone wiring (ref + addEventListener per repo guidance) ──
  const stageFiles = useCallback(
    (files: File[]) => {
      if (!planLoaded || files.length === 0) return;
      const { rejected } = addFiles(files);
      if (rejected > 0) {
        show({
          message: canBulk
            ? `You can upload up to ${maxFiles} files at a time.`
            : 'Bulk upload is a Pro feature — you can upload 1 file at a time.',
          isError: true,
        });
      }
    },
    [planLoaded, addFiles, show, canBulk, maxFiles],
  );

  const handleDropChange = useCallback(
    (e: Event) => {
      const el = e.currentTarget as any;
      el.error = '';
      stageFiles((el.files || []) as File[]);
      el.value = '';
    },
    [stageFiles],
  );

  const handleDropRejected = useCallback(
    (e: Event) => {
      const el = e.currentTarget as any;
      el.error = accept ? `Only these file types are allowed: ${accept}` : 'File type not accepted';
    },
    [accept],
  );

  const dropZoneElRef = useRef<HTMLElement | null>(null);
  const setDropZoneRef = useCallback(
    (el: HTMLElement | null) => {
      if (dropZoneElRef.current) {
        dropZoneElRef.current.removeEventListener('change', handleDropChange);
        dropZoneElRef.current.removeEventListener('droprejected', handleDropRejected);
      }
      dropZoneElRef.current = el;
      if (el) {
        el.addEventListener('change', handleDropChange);
        el.addEventListener('droprejected', handleDropRejected);
      }
    },
    [handleDropChange, handleDropRejected],
  );

  // Directory drops: s-drop-zone only surfaces plain files, so intercept drops
  // that contain a folder, traverse the FileSystem entries, and stage the files.
  // Plain-file drops fall through untouched; gated on webkitGetAsEntry so jsdom
  // (which lacks it) is unaffected.
  const handleWrapperDragOver = useCallback((e: DragEvent) => {
    if (e.dataTransfer) e.preventDefault();
  }, []);

  const handleWrapperDrop = useCallback(
    (e: DragEvent) => {
      const dt = e.dataTransfer;
      if (!dt || !dt.items || dt.items.length === 0) return;
      const entries: any[] = [];
      let hasDirectory = false;
      for (let i = 0; i < dt.items.length; i++) {
        const getEntry = (dt.items[i] as any).webkitGetAsEntry?.bind(dt.items[i]);
        if (!getEntry) return; // unsupported → let s-drop-zone handle it
        const entry = getEntry();
        if (entry) {
          entries.push(entry);
          if (entry.isDirectory) hasDirectory = true;
        }
      }
      if (!hasDirectory) return; // plain files → fall through to s-drop-zone
      e.preventDefault();
      e.stopPropagation();
      collectFilesFromEntries(entries).then((files) => stageFiles(files));
    },
    [stageFiles],
  );

  const dropWrapperElRef = useRef<HTMLElement | null>(null);
  const setDropWrapperRef = useCallback(
    (el: HTMLElement | null) => {
      if (dropWrapperElRef.current) {
        dropWrapperElRef.current.removeEventListener('drop', handleWrapperDrop as any, true);
        dropWrapperElRef.current.removeEventListener(
          'dragover',
          handleWrapperDragOver as any,
          true,
        );
      }
      dropWrapperElRef.current = el;
      if (el) {
        el.addEventListener('drop', handleWrapperDrop as any, true);
        el.addEventListener('dragover', handleWrapperDragOver as any, true);
      }
    },
    [handleWrapperDrop, handleWrapperDragOver],
  );

  const openProPlan = useCallback(() => {
    close();
    router.push('/plans');
  }, [close, router]);

  // ── Uploaded-tab filtering ──
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(() => defaultChip);

  const filteredUploaded = useMemo(() => {
    const list = uploadedFiles || [];
    const q = search.trim().toLowerCase();
    return list.filter((f) => {
      const name = (f.metadata?.name || '').toLowerCase();
      const nameMatch = !q || name.includes(q);
      const typeMatch = typeFilter === 'all' || f.type === typeFilter;
      return nameMatch && typeMatch;
    });
  }, [uploadedFiles, search, typeFilter]);

  // ── Renderers ──
  const renderStagedRow = (item: UploadItem) => {
    const durationLabel = formatDuration(item.meta.duration);
    const isValidationError = item.status === 'error_validation';
    const isError = item.status === 'error';
    const isDone = item.status === 'done';
    const isUploadingRow = item.status === 'uploading' || item.status === 'getting_params';

    return (
      <div className='upload-file-row' key={item.id}>
        <div className='upload-file-row__icon'>
          {previewUrlsRef.current.get(item.id) ? (
            <img
              className='upload-file-row__thumb'
              src={previewUrlsRef.current.get(item.id)}
              alt=''
            />
          ) : (
            <FontAwesomeIcon
              icon={getIconByMediaType(item.meta.type)}
              color={isValidationError || isError ? '#d72c0d' : '#6371c7'}
            />
          )}
        </div>
        <div className='upload-file-row__body'>
          <div className='upload-file-row__name' title={item.meta.name}>
            {item.meta.name}
          </div>
          <div className='upload-file-row__meta'>
            {formatSize(item.meta.size)}
            {durationLabel ? ` · ${durationLabel}` : ''}
          </div>
          {(isValidationError || isError) && item.errorMessage && (
            <div className='upload-file-row__error'>{item.errorMessage}</div>
          )}
          {isUploadingRow && (
            <div className='upload-progress'>
              <div
                className='upload-progress__fill'
                style={{ width: `${Math.round(item.percent)}%` }}
              />
            </div>
          )}
        </div>
        <div className='upload-file-row__actions'>
          {isUploadingRow && (
            <>
              <span className='upload-file-row__pct'>{Math.round(item.percent)}%</span>
              <s-button
                variant='tertiary'
                icon='x'
                accessibilityLabel='Cancel upload'
                onClick={() => cancel(item.id)}
              />
            </>
          )}
          {isDone && <FontAwesomeIcon icon={faCheck} color='#008060' />}
          {isError && (
            <s-button variant='tertiary' onClick={() => retry(item.id)}>
              Retry
            </s-button>
          )}
          {!isUploadingRow && !isDone && (
            <s-button
              variant='tertiary'
              icon='x'
              accessibilityLabel='Remove file'
              onClick={() => removeFile(item.id)}
            />
          )}
        </div>
      </div>
    );
  };

  const renderUploadedRow = (file: MediaFile) => {
    const isImage = file.type === 'image';
    const thumb = file.storage?.assets?.thumbnail?.secure_url;
    // When the caller constrained the picker to a type, other-type files are
    // browsable but not selectable.
    const selectable = !modalType || file.type === modalType;
    return (
      <div className='upload-file-row' key={file._id}>
        <div className='upload-file-row__icon'>
          {isImage && thumb ? (
            <img className='upload-file-row__thumb' src={thumb} alt='' />
          ) : (
            <FontAwesomeIcon icon={getIconByMediaType(file.metadata?.type)} color='#6371c7' />
          )}
        </div>
        <div className='upload-file-row__body'>
          <div className='upload-file-row__name' title={file.metadata?.name}>
            {file.metadata?.name}
          </div>
        </div>
        {selectable && (
          <div className='upload-file-row__actions'>
            <s-button
              variant='secondary'
              onClick={() => {
                primaryAction?.onAction?.(file);
                close();
              }}
            >
              Select
            </s-button>
          </div>
        )}
      </div>
    );
  };

  const deviceHelper = !planLoaded
    ? 'Loading upload limits…'
    : canBulk
    ? `Up to ${maxFiles} files${sizeLimitMb ? ` · ${sizeLimitMb} MB each` : ''}`
    : `1 file${sizeLimitMb ? ` · ${sizeLimitMb} MB` : ''}`;

  return (
    <s-modal id={modalId} ref={setModalRef as any} heading={heading} size='base'>
      <div className='upload-modal'>
        {pickerContext && (
          <div className='upload-tabs'>
            <button
              type='button'
              className={`upload-tab${activeTab === 'my-device' ? ' upload-tab--active' : ''}`}
              onClick={() => selectTab('my-device')}
            >
              My device
            </button>
            <button
              type='button'
              className={`upload-tab${activeTab === 'uploaded' ? ' upload-tab--active' : ''}`}
              onClick={() => selectTab('uploaded')}
            >
              Uploaded
            </button>
            {canGenerate && (
              <button
                type='button'
                className={`upload-tab${activeTab === 'generate' ? ' upload-tab--active' : ''}`}
                onClick={() => selectTab('generate')}
              >
                Generate
              </button>
            )}
          </div>
        )}

        {activeTab === 'my-device' && (
          <div className='upload-device'>
            <div ref={setDropWrapperRef as any}>
              <s-drop-zone
                ref={setDropZoneRef as any}
                label='Drag and drop files, or click to browse'
                accept={accept}
                multiple={canBulk || undefined}
                disabled={!planLoaded || undefined}
              />
            </div>
            <s-text color='subdued'>{deviceHelper}</s-text>

            {items.length > 0 && (
              <div className='upload-file-list'>{items.map(renderStagedRow)}</div>
            )}

            {showUpsell && (
              <s-banner tone='info'>
                <s-text>
                  Want to upload multiple files at once?{' '}
                  <s-link onClick={openProPlan}>Upgrade to Pro.</s-link>
                </s-text>
              </s-banner>
            )}
          </div>
        )}

        {activeTab === 'uploaded' && (
          <div className='upload-uploaded'>
            <s-search-field
              label='Search files'
              placeholder='Search files'
              labelAccessibilityVisibility='exclusive'
              value={search}
              onInput={(e: any) => setSearch(e.currentTarget.value)}
            />

            <div className='upload-chips'>
              {TYPE_CHIPS.map((chip) => (
                <s-clickable-chip
                  key={chip.id}
                  color={typeFilter === chip.id ? 'strong' : 'base'}
                  accessibilityLabel={`Filter by ${chip.label}`}
                  onClick={() => setTypeFilter(chip.id)}
                >
                  {chip.label}
                </s-clickable-chip>
              ))}
            </div>

            <div className='upload-file-list upload-file-list--scroll'>
              {(uploadedFiles || []).length === 0 ? (
                <div className='upload-empty'>
                  <s-text color='subdued'>
                    No uploaded files yet — switch to My device to upload.
                  </s-text>
                </div>
              ) : filteredUploaded.length === 0 ? (
                <div className='upload-empty'>
                  <s-text color='subdued'>No files match your search.</s-text>
                </div>
              ) : (
                filteredUploaded.map(renderUploadedRow)
              )}
            </div>
          </div>
        )}

        {activeTab === 'generate' && (
          <div className='voicegen'>
            <s-text-field
              label='Tag phrase'
              placeholder='Skyline on the beat'
              details='Short works best — one or two words and your name.'
              value={tagText}
              maxLength={120}
              onInput={(e: React.FormEvent<HTMLElement>) =>
                setTagText((e.currentTarget as HTMLInputElement).value)
              }
            />

            <div className='voicegen__field'>
              <span className='voicegen__label'>Voice</span>
              {/* Cards rather than a dropdown: a producer picks a voice by how it
                  sounds, and a <select> hides nine options behind a click. */}
              <div className='voicegen__voices' role='radiogroup' aria-label='Voice'>
                {VOICE_TAG_VOICES.map((voice) => {
                  const selected = voice.id === voiceId;
                  return (
                    <button
                      type='button'
                      key={voice.id}
                      role='radio'
                      aria-checked={selected}
                      className={`voicegen__voice${selected ? ' voicegen__voice--on' : ''}`}
                      onClick={() => setVoiceId(voice.id)}
                    >
                      <span className='voicegen__voice-name'>{voice.name}</span>
                      <span className='voicegen__voice-meta'>
                        {voice.tone} · {voice.gender === 'male' ? 'M' : 'F'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className='voicegen__field'>
              <span className='voicegen__label'>
                Delivery speed
                <span className='voicegen__value'>
                  {SPEED_STOPS.find((s) => s.value === speed)?.label ?? `${speed}x`}
                </span>
              </span>
              <input
                className='voicegen__slider'
                type='range'
                min={0}
                max={SPEED_STOPS.length - 1}
                step={1}
                value={SPEED_STOPS.findIndex((s) => s.value === speed)}
                aria-label='Delivery speed'
                onChange={(e) => setSpeed(SPEED_STOPS[Number(e.currentTarget.value)].value)}
              />
              <div className='voicegen__ticks'>
                <span>Slower</span>
                <span>Faster</span>
              </div>
              {/* The distinction trips people up, so say it once, here. */}
              <s-text color='subdued'>
                Changes how fast the voice talks. To make it sound deeper, set the tag character in
                Settings.
              </s-text>
            </div>

            <s-button
              variant='primary'
              loading={generating || undefined}
              disabled={tagText.trim().length < 2 || generating || undefined}
              onClick={generateTakes}
            >
              {takes.length ? 'Generate again' : 'Generate 3 takes'}
            </s-button>

            {takes.length === 0 ? (
              <div className='voicegen__empty'>
                <s-text color='subdued'>
                  {generating
                    ? 'Recording your takes…'
                    : 'Type your tag, pick a voice, and we will make three takes to choose from.'}
                </s-text>
              </div>
            ) : (
              <div className='voicegen__takes'>
                {takes.map((take, index) => {
                  const playing = playingId === take.fileId;
                  return (
                    <div
                      className={`voicegen__take${playing ? ' voicegen__take--on' : ''}`}
                      key={take.fileId}
                    >
                      <button
                        type='button'
                        className='voicegen__play'
                        aria-label={playing ? `Stop take ${index + 1}` : `Play take ${index + 1}`}
                        onClick={() => toggleTake(take)}
                      >
                        {playing ? '■' : '▶'}
                      </button>
                      <span className='voicegen__take-name'>Take {index + 1}</span>
                      <s-button variant='secondary' onClick={() => useTake(take)}>
                        Use this
                      </s-button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {activeTab === 'my-device' && (
        <>
          <s-button
            slot='secondary-actions'
            onClick={() => {
              clearAll();
              close();
            }}
          >
            Cancel
          </s-button>
          <s-button
            slot='primary-action'
            variant='primary'
            loading={isUploading || undefined}
            disabled={uploadableCount === 0 || undefined}
            onClick={() => startUpload()}
          >
            {`Upload (${uploadableCount})`}
          </s-button>
        </>
      )}
    </s-modal>
  );
};

export default UploadMediaModal;
